import { useRef, useState } from 'react';
import { SmilePlus } from 'lucide-react';
import { useUser } from '../../context/UserContext';
import EmojiPicker from './EmojiPicker';
import { useCoarsePointer } from './useCoarsePointer';
import type { Reaction } from './useChatReactions';

interface ChatReactionsProps {
  messageId: string;
  reactions: Reaction[];
  onAdd: (emoji: string) => void;
  onRemove: (emoji: string) => void;
}

interface GroupedReaction {
  emoji: string;
  count: number;
  users: string[];
  userIds: string[];
  hasCurrentUser: boolean;
}

function groupReactions(reactions: Reaction[], currentUserId: string | undefined): GroupedReaction[] {
  const groups = new Map<string, GroupedReaction>();

  for (const reaction of reactions) {
    const existing = groups.get(reaction.emoji);
    if (existing) {
      existing.count++;
      existing.users.push(reaction.user_name);
      existing.userIds.push(reaction.user_id);
      if (reaction.user_id === currentUserId) {
        existing.hasCurrentUser = true;
      }
    } else {
      groups.set(reaction.emoji, {
        emoji: reaction.emoji,
        count: 1,
        users: [reaction.user_name],
        userIds: [reaction.user_id],
        hasCurrentUser: reaction.user_id === currentUserId,
      });
    }
  }

  return Array.from(groups.values());
}

function ReactionBadge({
  group,
  onClick,
}: {
  group: GroupedReaction;
  onClick: () => void;
}) {
  const [showTooltip, setShowTooltip] = useState(false);
  const touch = useCoarsePointer();

  return (
    <div className="relative">
      <button
        onClick={onClick}
        onMouseEnter={() => setShowTooltip(true)}
        onMouseLeave={() => setShowTooltip(false)}
        className="pressable inline-flex items-center gap-1"
        style={{
          padding: touch ? '7px 11px' : '2px 9px',
          borderRadius: 'var(--radius-pill)',
          background: group.hasCurrentUser ? 'var(--accent-soft)' : 'var(--surface-raised)',
          border: group.hasCurrentUser
            ? '1px solid var(--border-focus)'
            : '1px solid var(--border-hairline)',
          cursor: 'pointer',
          transition: 'background var(--dur-base) var(--ease-out), border-color var(--dur-base) var(--ease-out)',
        }}
        title={group.hasCurrentUser ? 'Remove reaction' : 'Add reaction'}
      >
        <span className="leading-none" style={{ fontSize: 'var(--text-xs)' }}>{group.emoji}</span>
        <span
          className="t-num leading-none"
          style={{
            fontSize: 'var(--text-2xs)',
            fontWeight: 'var(--weight-medium)' as React.CSSProperties['fontWeight'],
            color: group.hasCurrentUser ? 'var(--accent-text)' : 'var(--text-muted)',
          }}
        >
          {group.count}
        </span>
      </button>

      {/* Tooltip showing who reacted */}
      {showTooltip && (
        <div
          className="absolute bottom-full mb-1.5 left-1/2 -translate-x-1/2 whitespace-nowrap z-50 pointer-events-none ck-menu-pop"
          style={{
            padding: '3px 8px',
            fontSize: 'var(--text-2xs)',
            color: 'var(--text-body)',
            background: 'var(--surface-raised)',
            border: '1px solid var(--border-hairline)',
            borderRadius: 'var(--radius-xs)',
            boxShadow: 'var(--shadow-pop)',
          }}
        >
          {group.users.join(', ')}
        </div>
      )}
    </div>
  );
}

export default function ChatReactions({ messageId, reactions, onAdd, onRemove }: ChatReactionsProps) {
  const { user } = useUser();
  const [showPicker, setShowPicker] = useState(false);
  const touch = useCoarsePointer();
  const addButtonRef = useRef<HTMLButtonElement>(null);

  const grouped = groupReactions(reactions, user?.id);

  // Don't render anything if no reactions and picker isn't open
  if (grouped.length === 0 && !showPicker) return null;

  return (
    <div className="flex flex-wrap items-center gap-1 ml-[42px] mt-1 relative">
      {grouped.map(group => (
        <ReactionBadge
          key={`${messageId}-${group.emoji}`}
          group={group}
          onClick={() => {
            if (group.hasCurrentUser) {
              onRemove(group.emoji);
            } else {
              onAdd(group.emoji);
            }
          }}
        />
      ))}

      {/* Add reaction button */}
      <div className="relative">
        <button
          ref={addButtonRef}
          onClick={() => setShowPicker(prev => !prev)}
          className="hover-wash pressable inline-flex items-center justify-center"
          style={{
            // touch: thumb-sized
            width: touch ? '32px' : '24px',
            height: touch ? '32px' : '24px',
            border: 'none',
            background: 'transparent',
            borderRadius: 'var(--radius-pill)',
            color: 'var(--text-faint)',
            cursor: 'pointer',
          }}
          title="Add reaction"
        >
          <SmilePlus size={14} />
        </button>

        <EmojiPicker
          visible={showPicker}
          anchorEl={addButtonRef.current}
          onSelect={(emoji) => onAdd(emoji)}
          onClose={() => setShowPicker(false)}
        />
      </div>
    </div>
  );
}
